import { IndexedDBStorage } from '../../../libs/helpers/indexdb'

const STORAGE_KEY = 'omneo-wishlists'
const storage = new IndexedDBStorage('omneo-db', 'wishlists')

/**
 * Grabs and combines both parent and child wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Array>}
 * @throws {Error} If initialization fails or client is invalid
 */
export const getAllWishlists = async (omneoClient, useCache = false) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }

  try {
    await storage.init()
  } catch (error) {
    console.error('Failed to initialize storage:', error)
    // Continue without cache if storage fails
  }

  if (useCache) {
    try {
      const cachedData = await storage.getItem(STORAGE_KEY)
      if (cachedData && Array.isArray(cachedData)) {
        return cachedData
      }
    } catch (error) {
      console.error('Failed to retrieve cached wishlists:', error)
      // Continue without cache
    }
  }

  const parentWishlists = []
  const childWishlists = []

  let children = []
  try {
    children = await omneoClient.profileClient.getChildren() || []
  } catch (error) {
    console.error('Failed to fetch children profiles:', error)
    children = []
  }

  const childPromises = children.map((child) =>
    (async () => {
      try {
        omneoClient.client.profile.Connection(child.id)

        let [wishlist, profileData] = await Promise.all([
          omneoClient.getOrCreateList(undefined, 'wishlist'),
          omneoClient.client.profile.get()
        ])

        if (!wishlist || !profileData) {
          console.warn(`Failed to fetch complete data for child ${child.id}`)
          return
        }

        child.connector = { ...profileData, ...child.connector }

        if (wishlist) {
          const firstName = child.connector.first_name || 'Unknown'
          wishlist = {
            ...wishlist,
            name: `${firstName}${firstName.endsWith('s') ? "'" : "'s"} Wishlist`,
            profile: child
          }
          childWishlists.push(wishlist)
        }
      } catch (error) {
        console.error(`Failed to process wishlist for child ${child.id}:`, error)
      }
    })()
  )

  await Promise.all([
    (async () => {
      try {
        const wishlists = await omneoClient.profileClient.getWishlists()
        if (Array.isArray(wishlists)) {
          parentWishlists.push(...wishlists)
        } else {
          console.warn('Parent wishlists response was not an array:', wishlists)
        }
      } catch (error) {
        console.error('Failed to fetch parent wishlists:', error)
      }
    })(),
    ...childPromises
  ])

  const wishlists = [...parentWishlists, ...childWishlists].filter(Boolean)

  try {
    await storage.setItem(STORAGE_KEY, wishlists)
  } catch (error) {
    console.error('Failed to cache wishlists:', error)
  }

  return wishlists
}

/**
 * Removes a product from all wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {Array} wishlists - Array of wishlists to process
 * @param {string|number} productId - ID of the product to remove
 * @throws {Error} If client is invalid
 */
export const removeProductFromAllWishlists = async (omneoClient, wishlists, productId) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }
  if (!Array.isArray(wishlists)) {
    throw new Error('Wishlists must be an array')
  }
  if (!productId) {
    throw new Error('Product ID is required')
  }

  const promises = wishlists.reduce((acc, wishlist) => {
    if (!wishlist || !Array.isArray(wishlist.items)) {
      return acc
    }

    const item = wishlist.items.find((i) => String(i?.meta?.productId) === String(productId))
    if (item) {
      acc.push(
        (async () => {
          try {
            let profile = omneoClient.profileClient
            if (wishlist.profileId) {
              omneoClient.client.profile.Connection(Number(wishlist.profileId))
              profile = omneoClient
            }

            await profile.removeWishlistItem(wishlist.id, item.id)
          } catch (error) {
            console.error(`Failed to remove product ${productId} from wishlist ${wishlist.id}:`, error)
          }
        })()
      )
    }
    return acc
  }, [])

  await Promise.all(promises)
}

/**
 * Removes a variant from all wishlists
 * @param {Object} omneoClient - The Omneo client instance
 * @param {Array} wishlists - Array of wishlists to process
 * @param {string|number} variantId - ID of the variant to remove
 * @throws {Error} If client is invalid
 */
export const removeVariantFromAllWishlists = async (omneoClient, wishlists, variantId) => {
  if (!omneoClient) {
    throw new Error('Omneo client is required')
  }
  if (!Array.isArray(wishlists)) {
    throw new Error('Wishlists must be an array')
  }
  if (!variantId) {
    throw new Error('Variant ID is required')
  }

  const promises = wishlists.reduce((acc, wishlist) => {
    if (!wishlist || !Array.isArray(wishlist.items)) {
      return acc
    }

    const item = wishlist.items.find((i) => String(i?.meta?.variantId) === String(variantId))
    if (item) {
      acc.push(
        (async () => {
          try {
            let profile = omneoClient.profileClient
            if (wishlist.profileId) {
              omneoClient.client.profile.Connection(Number(wishlist.profileId))
              profile = omneoClient
            }

            await profile.removeWishlistItem(wishlist.id, item.id)
          } catch (error) {
            console.error(`Failed to remove variant ${variantId} from wishlist ${wishlist.id}:`, error)
          }
        })()
      )
    }
    return acc
  }, [])

  await Promise.all(promises)
}
